import React, { useEffect, useState } from "react";
import { ExerciseTypeContainer } from "./ExerciseTypeContainer";
import { useExercise } from "../adapter/StudentExerciseContext";
import {
  ExerciseItem,
  getExerciseContent,
  getExerciseAnswerData,
  type MultipleChoiceAnswerData,
} from "@/services/student/exercise.interfaces";
import { Input } from "@/components/ui";
import { Check, CheckCheck } from "lucide-react";
import {
  getMultipleChoiceProgressMessage,
  getMultipleChoiceSelectionState,
  shouldDisableUncheckedMultipleChoiceOption,
} from "./multipleChoiceSelection";
import { MultipleChoiceSelectionProgress } from "./MultipleChoiceSelectionProgress";

interface VerifyResult {
  isCorrect: boolean;
  feedback?: string;
  expectedAnswer?: string[] | string;
}

type MultipleChoicesProps = {
  item: ExerciseItem;
  moduleId?: string;
  onAnswer?: (
    answer: string[],
    item: ExerciseItem
  ) => Promise<VerifyResult> | VerifyResult;
  onVerifyClick?: (selectedOptions: string[]) => void;
  readonly?: boolean;
  previousAnswer?: {
    selectedOptions: string[];
    isCorrect: boolean;
    correctOptions?: string[];
  };
};

interface MultipleChoiceContent {
  question?: string;
  multipleCorrectAnswers?: boolean;
  options?: Array<{
    id: string;
    text: string;
    order: number;
  }>;
}

export const MultipleChoices: React.FC<MultipleChoicesProps> = (props) => {
  const { item, moduleId, readonly = false, previousAnswer } = props;
  const content = getExerciseContent<MultipleChoiceContent>(item);
  const answerData = getExerciseAnswerData<MultipleChoiceAnswerData>(item);
  const multipleResponses = answerData.multipleResponses;

  return (
    <ExerciseTypeContainer
      item={{ ...item, type: "multiple_choice" }}
      moduleId={moduleId}
      title1={
        multipleResponses
          ? "Selecione a(s) opção(ões) correta(s):"
          : "Selecione a opção correta:"
      }
      title2={content.question || item.title}
      exerciseNumber={item.id}
      hideVerifyButton={readonly}
      isVerified={Boolean(previousAnswer)}
      isCorrect={previousAnswer?.isCorrect ?? false}
    >
      <MultipleChoicesBody {...props} />
    </ExerciseTypeContainer>
  );
};

const MultipleChoicesBody: React.FC<MultipleChoicesProps> = ({
  item,
  onAnswer,
  onVerifyClick,
  readonly = false,
  previousAnswer,
}) => {
  const { verify, registerVerifyHandler, setCanVerify } = useExercise();
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() => {
    const raw = previousAnswer?.selectedOptions;
    if (Array.isArray(raw) && raw.length > 0) return raw.map((x) => String(x));
    return [];
  });
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(() => Boolean(previousAnswer));
  const [isCorrect, setIsCorrect] = useState(
    () => previousAnswer?.isCorrect ?? false
  );
  const [feedback, setFeedback] = useState<string>("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [correctOptionsFromBackend, setCorrectOptionsFromBackend] = useState<
    string[]
  >(
    readonly && previousAnswer?.correctOptions
      ? previousAnswer.correctOptions
      : []
  );

  const content = getExerciseContent<MultipleChoiceContent>(item);
  const answerData = getExerciseAnswerData<MultipleChoiceAnswerData>(item);
  const multipleResponses = answerData.multipleResponses;
  const options = content.options || [];
  const selectionState = getMultipleChoiceSelectionState({
    multipleResponses,
    correctOptionCount: answerData.correctOptionCount,
    selectedCount: selectedOptions.length,
  });
  const progressMessage = getMultipleChoiceProgressMessage(selectionState);
  const isVerifiedAndCorrect = isVerified && isCorrect;

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setSelectedOptions((prevSelected) => {
      if (multipleResponses) {
        if (prevSelected.includes(value)) {
          return prevSelected.filter((option) => option !== value);
        }
        if (
          selectionState.useExactCount &&
          selectionState.requiredCount != null &&
          prevSelected.length >= selectionState.requiredCount
        ) {
          return prevSelected;
        }
        return [...prevSelected, value];
      }
      return [value];
    });
    setIsVerified(false);
    setIsCorrect(false);
    setFeedback("");
    setValidationError(null);
  };

  const handleVerify = async () => {
    if (!selectionState.canVerify) {
      if (selectionState.useExactCount && selectionState.requiredCount != null) {
        setValidationError(
          `Selecione exatamente ${selectionState.requiredCount} opção(ões) antes de verificar.`
        );
      } else {
        setValidationError(
          "Por favor, selecione pelo menos uma opção antes de verificar."
        );
      }
      return;
    }

    setValidationError(null);
    setIsVerifying(true);

    if (onVerifyClick) {
      onVerifyClick(selectedOptions);
    }

    try {
      let result: VerifyResult;

      if (onAnswer) {
        result = await onAnswer(selectedOptions, item);
      } else {
        result = await verify(selectedOptions);
      }

      setIsCorrect(result.isCorrect);
      setFeedback(result.feedback || "");
      setIsVerified(true);

      if (!result.isCorrect && result.expectedAnswer != null) {
        const raw = result.expectedAnswer;
        const arr = Array.isArray(raw)
          ? raw.map((x) => String(x))
          : [String(raw)];
        setCorrectOptionsFromBackend(arr);
      } else {
        setCorrectOptionsFromBackend([]);
      }
    } catch (error) {
      console.error("Erro ao verificar resposta:", error);
      setFeedback(
        "Ocorreu um erro ao verificar sua resposta. Tente novamente."
      );
      setIsCorrect(false);
      setIsVerified(true);
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    setCanVerify(selectionState.canVerify);
  }, [selectionState.canVerify, setCanVerify]);

  useEffect(() => {
    registerVerifyHandler(handleVerify);
  });

  return (
    <>
      <span className="flex items-center gap-1 text-sm text-gray-700 mb-2" aria-hidden>
        {multipleResponses ? (
          <CheckCheck className="w-4 h-4" />
        ) : (
          <Check className="w-4 h-4" />
        )}
        {multipleResponses ? "Múltiplas Respostas" : "Resposta Única"}
      </span>

      {multipleResponses && (
        <MultipleChoiceSelectionProgress
          selectionState={selectionState}
          progressMessage={progressMessage}
          showLimitHint={
            selectionState.isAtSelectionLimit &&
            !readonly &&
            !isVerifiedAndCorrect
          }
        />
      )}

      <div
        className="flex flex-col gap-1 mt-4"
        role={multipleResponses ? "group" : "radiogroup"}
        aria-label={
          content.question ||
          (multipleResponses
            ? "Opções de resposta (múltipla escolha)"
            : "Opções de resposta (escolha única)")
        }
      >
        {options.map((option) => {
          const optionId = String(option.id);
          const isOptionDisabled = shouldDisableUncheckedMultipleChoiceOption({
            optionId,
            selectedOptions,
            state: selectionState,
            isVerifying,
            readonly,
            isVerifiedAndCorrect,
          });

          return (
            <label
              key={option.id}
              className={`flex items-center gap-3 min-h-[44px] cursor-pointer hover:bg-gray-100 px-3 py-2 rounded ${
                isOptionDisabled && !selectedOptions.includes(optionId)
                  ? "opacity-60 cursor-not-allowed"
                  : ""
              } ${
                isVerified && isCorrect && selectedOptions.includes(optionId)
                  ? "bg-green-100"
                  : ""
              } ${
                isVerified && !isCorrect && selectedOptions.includes(optionId)
                  ? "bg-red-100"
                  : ""
              } ${
                isVerified &&
                !isCorrect &&
                correctOptionsFromBackend.includes(optionId)
                  ? "border-2 border-green-500 bg-green-50"
                  : ""
              }`}
            >
              <Input
                type={multipleResponses ? "checkbox" : "radio"}
                name={multipleResponses ? undefined : `exerciseOption-${item.id}`}
                value={option.id}
                checked={selectedOptions.includes(optionId)}
                onChange={handleOptionChange}
                disabled={isOptionDisabled}
                className="w-4 h-4 shrink-0"
                aria-describedby={
                  multipleResponses
                    ? undefined
                    : `option-desc-${item.id}-${option.id}`
                }
              />
              <span
                id={
                  multipleResponses
                    ? undefined
                    : `option-desc-${item.id}-${option.id}`
                }
                className="text-base text-gray-900"
              >
                {option.text}
              </span>
            </label>
          );
        })}
      </div>

      {validationError && (
        <p className="text-red-600 text-sm mt-2" role="alert">
          {validationError}
        </p>
      )}

      {isVerified && feedback && (
        <div className="mt-2">
          <p className="text-base text-gray-800">{feedback}</p>
        </div>
      )}
    </>
  );
};
